import React, { useCallback, useEffect, useState } from 'react';

import { Badge, Group, Modal, Text, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCircleCheckFilled, IconCircleOff } from '@tabler/icons-react';
import CurrentPackagesTableCell from 'components/AthletePackages/CurrentPackagesTableCell';
import AthleteStatusBadge from 'components/status-badges/AthleteStatusBadge';
import Table from 'components/Table';
import { useAuth } from 'context/auth-context';
import useAthletes from 'hooks/athlete/useAthletes';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { AthleteList } from 'types';
import { formatPhoneNumber } from 'utils/formatters';

import DeleteAthleteModal from './DeleteAthleteModal';
import GLMLinkStatusCell from './GLMLinkStatusCell';
import AthleteActionsMenu from '../../AthleteDetail/components/AthleteActionsMenu';

interface IAthletesTableContainerProps {
  searchFilters: {
    searchTerm: string;
    isArrangeByLastName: boolean;
    athleteStatus: string;
    loginEnabled: string;
    packages: string[];
    glmLinkState?: string;
  };
  orderingValue: string;
  setOrderingValue: React.Dispatch<React.SetStateAction<string>>;
  handleEditAthlete: (id: string) => void;
  handleCreateLinkedProfile: (primaryAthleteId: string) => void;
  handleCreateDependentProfile: (primaryAthleteId: string) => void;
  permission?: boolean;
}

const AthletesTableContainer = ({
  searchFilters,
  orderingValue,
  setOrderingValue,
  handleEditAthlete,
  handleCreateLinkedProfile,
  handleCreateDependentProfile,
  permission = true,
}: IAthletesTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();

  const { session } = useAuth();

  const [tableData, setTableData] = useState<AthleteList[]>([]);
  const [selectedAthleteId, setSelectedAthleteId] = useState<string>('');
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const ordering = orderingValue.startsWith('-') ? orderingValue.slice(1) : orderingValue;
  const ascending = !orderingValue.startsWith('-');

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useAthletes({
      ...searchFilters,
      ordering: orderingValue,
    });

  const handleHeaderClick = useCallback(
    (header: string): void => {
      let orderingTemp = header;
      if (header === 'firstName' && searchFilters.isArrangeByLastName) {
        orderingTemp = 'lastName';
      }
      const sameHeader = ordering === orderingTemp;
      const ascendingTemp = sameHeader ? !ascending : true;
      setOrderingValue(ascendingTemp ? orderingTemp : `-${orderingTemp}`);
    },
    [ascending, ordering, searchFilters.isArrangeByLastName, setOrderingValue],
  );

  const handleNameClick = useCallback(
    (athlete: AthleteList): void => {
      navigate(`/users/athletes/${athlete.athleteId}/general-info`);
    },
    [navigate],
  );

  const handleDeleteAthlete = (athleteId: string): void => {
    setSelectedAthleteId(athleteId);
    openDeleteModal();
  };

  useEffect(() => {
    const header = searchFilters.isArrangeByLastName ? 'lastName' : 'firstName';
    const currentAscending = !orderingValue.startsWith('-');
    setOrderingValue(currentAscending ? header : `-${header}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters]);

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Name',
      accessor: 'firstName' as const,
      Cell: ({ row }: CellProps<AthleteList>) => (
        <Group noWrap spacing='xs'>
          <Text
            weight='bold'
            underline
            sx={{ cursor: 'pointer' }}
            onClick={() => handleNameClick(row.original)}
          >
            {row.original.fullName}
          </Text>
          {row.original.connectedType === 'Linked' && (
            <Badge color='kiloBlue' variant='filled' size='xs' radius='md'>
              Linked
            </Badge>
          )}
          {row.original.connectedType === 'Dependent' && (
            <Badge color='kiloBlue' variant='filled' size='xs' radius='md'>
              Dependent
            </Badge>
          )}
        </Group>
      ),
      minWidth: '100px',
    },
    {
      Header: 'Status',
      accessor: 'athleteStatus' as const,
      Cell: ({ value }: CellProps<AthleteList>) => <AthleteStatusBadge status={value} />,
    },
    {
      Header: 'Email',
      accessor: 'email' as const,
      Cell: ({ row }: CellProps<AthleteList>) => <Text underline>{row.original.email}</Text>,
    },
    {
      Header: 'Phone',
      accessor: 'phone' as const,
      Cell: ({ value }: CellProps<AthleteList>) => formatPhoneNumber(value),
    },
    {
      Header: 'Current packages',
      accessor: 'currentPackages' as const,
      Cell: ({ row }: CellProps<AthleteList>) => (
        <CurrentPackagesTableCell currentPackages={row.original.currentPackages ?? []} />
      ),
    },
    {
      Header: 'Has payment method',
      accessor: 'hasPaymentMethod' as const,
      Cell: ({ row }: CellProps<AthleteList>) =>
        row.original.hasPaymentMethod ? (
          <IconCircleCheckFilled size={theme.fontSizes.xl} style={{ color: theme.colors.kiloBlue[6] }} />
        ) : (
          <IconCircleOff size={theme.fontSizes.sm} />
        ),
    },
    ...(session?.gym?.wodupEnabled
      ? [
          {
            Header: 'Active on wodup',
            accessor: 'activeOnWodup' as const,
            Cell: ({ row }: CellProps<AthleteList>) =>
              row.original.activeOnWodup ? (
                <IconCircleCheckFilled
                  size={theme.fontSizes.xl}
                  style={{ color: theme.colors.kiloBlue[6] }}
                />
              ) : (
                <IconCircleOff size={theme.fontSizes.sm} />
              ),
          },
        ]
      : []),
    ...(session?.gym?.ghlV2Enabled
      ? [
          {
            Header: 'GLM',
            accessor: 'glmLinkState' as const,
            Cell: ({ row }: CellProps<AthleteList>) => <GLMLinkStatusCell athlete={row.original} />,
          },
        ]
      : []),
    {
      id: 'actions',
      disableSortBy: true,
      Cell: ({ row }: CellProps<AthleteList>) => (
        <AthleteActionsMenu
          athleteList={row.original}
          handleEdit={handleEditAthlete}
          handleDelete={handleDeleteAthlete}
          handleCreateLinkedProfile={handleCreateLinkedProfile}
          handleCreateDependentProfile={handleCreateDependentProfile}
          disabled={!permission}
          wodupEnabled={!!session?.gym?.wodupEnabled}
        />
      ),
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['firstName', 'email', 'athleteStatus', 'loginEnabled', 'phone', 'glmLinkState']}
        handleHeaderClick={handleHeaderClick}
        ascending={ascending}
        ordering={ordering}
      />
      <Modal
        opened={isDeleteModalOpen}
        onClose={closeDeleteModal}
        title={<Title order={4}>Delete Athlete</Title>}
        trapFocus
        centered
        size='auto'
      >
        <DeleteAthleteModal athleteId={selectedAthleteId} handleClose={closeDeleteModal} />
      </Modal>
    </>
  );
};

export default AthletesTableContainer;
